import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import { HomeIcon, BuildingOffice2Icon, DocumentTextIcon, CheckCircleIcon, EyeIcon, PrinterIcon } from "@heroicons/react/24/outline";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { fetchOrgCharts, exportOrgChartRevisionPDF } from "../../../api/quality/orgCharts";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { toast } from "react-hot-toast";

/**
 * HSE/KS Dashboard - Overview page
 * Shows KPI overview, tasks, notifications, and recent events summary
 */
export const HseKsDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [orgCharts, setOrgCharts] = useState([]);
  const [loadingOrgCharts, setLoadingOrgCharts] = useState(true);

  useEffect(() => {
    fetchOrgChartsData();
  }, []);

  const fetchOrgChartsData = async () => {
    try {
      setLoadingOrgCharts(true);
      const response = await fetchOrgCharts({ per_page: 5, page: 1 });
      setOrgCharts(response.data || []);
    } catch (err) {
      console.error("Error fetching org charts:", err);
    } finally {
      setLoadingOrgCharts(false);
    }
  };

  const handleExportPDF = async (orgChartId, revisionId) => {
    try {
      const response = await exportOrgChartRevisionPDF(orgChartId, revisionId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `organisasjonskart-revisjon-${revisionId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(t("PDF eksportert"));
    } catch (err) {
      toast.error(t("Kunne ikke eksportere PDF"));
    }
  };

  return (
    <DashboardHeader
      title="HMS & KS Dashboard"
      subtitle="Oversikt over KPI, oppgaver, varsler og nylige hendelser"
    >
      <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {/* KPI Overview */}
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">KPI Oversikt</h2>
                <p className="text-gray-500">KPI-oversikt kommer snart...</p>
              </div>
            </div>

            {/* My Tasks */}
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Mine oppgaver</h2>
                <p className="text-gray-500">Oppgaver kommer snart...</p>
              </div>
            </div>

            {/* Notifications */}
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Varsler</h2>
                <p className="text-gray-500">Varsler kommer snart...</p>
              </div>
            </div>
          </div>

          {/* Organisasjonskart - Latest Approved Revision */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <BuildingOffice2Icon className="h-5 w-5 mr-2 text-blue-600" />
                  Organisasjonskart
                </h2>
                <Link
                  to="/dashboard/hse-ks/org-charts"
                  className="text-sm text-indigo-600 hover:text-indigo-800"
                >
                  Se alle →
                </Link>
              </div>
              
              {loadingOrgCharts ? (
                <ResourceLoading size="sm" />
              ) : orgCharts.length === 0 ? (
                <p className="text-gray-500">Ingen organisasjonskart funnet</p>
              ) : (
                <div className="space-y-4">
                  {orgCharts
                    .filter(oc => oc.approved_revision)
                    .slice(0, 3)
                    .map((orgChart) => (
                      <div key={orgChart.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <DocumentTextIcon className="h-5 w-5 text-blue-600" />
                              <h3 className="text-sm font-medium text-gray-900">{orgChart.title}</h3>
                              {orgChart.approved_revision && (
                                <CheckCircleIcon className="h-4 w-4 text-green-500" title="Godkjent revisjon" />
                              )}
                            </div>
                            {orgChart.description && (
                              <p className="text-sm text-gray-500 mb-2">{orgChart.description}</p>
                            )}
                            {orgChart.approved_revision && (
                              <div className="flex items-center space-x-4 text-xs text-gray-500">
                                <span>
                                  Revisjon {orgChart.approved_revision.revision_number}
                                </span>
                                {orgChart.approved_revision.approved_at && (
                                  <span>
                                    Godkjent: {new Date(orgChart.approved_revision.approved_at).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center space-x-2 ml-4">
                            <Link
                              to={`/dashboard/hse-ks/org-charts/${orgChart.id}`}
                              className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                            >
                              <EyeIcon className="h-4 w-4 mr-1" />
                              Vis
                            </Link>
                            {orgChart.approved_revision && (
                              <button
                                onClick={() => handleExportPDF(orgChart.id, orgChart.approved_revision.id)}
                                className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                              >
                                <PrinterIcon className="h-4 w-4 mr-1" />
                                PDF
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>

          {/* Recent Events */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Nylige hendelser</h2>
              <p className="text-gray-500">Nylige hendelser kommer snart...</p>
            </div>
          </div>
        </div>
      </div>
    </DashboardHeader>
  );
};

